import {Stack, SxProps, Theme, Typography} from "@mui/material";
import {lazy, Suspense} from "react";
import {PageWrapper} from "../pages";
import {Navigate, Route, Routes} from "react-router-dom";
import {useDownBreakpoint} from "@hooks";
import {AppLogo, LoadingView} from "@components";
import {useSelector} from "react-redux";
import {Selectors} from "@core/store/selectors";
import {EmptyData} from "@components/EmptyData";
import {DangerousOutlined} from "@mui/icons-material";
import {translate} from "@util";

const HomePage = lazy(() => import("../pages/home"));
const AssetsPage = lazy(() => import("../pages/assets"));
const TicketsPage = lazy(() => import("../pages/tickets"));
const TasksPage = lazy(() => import("../pages/tasks"));
const CustomersPage = lazy(() => import("../pages/tenants"));
const GroupsPage = lazy(() => import("../pages/groups"));
const UsersPage = lazy(() => import("../pages/users"));
const UserDetailPage = lazy(() => import("../pages/users/detail"));
const AuditsPage = lazy(() => import("../pages/audits"));
const DocumentsPage = lazy(() => import("../pages/documents"));
const ArchivePage = lazy(() => import("../pages/archive"));
const MonitoringPage = lazy(() => import("../pages/monitoring"));
const ArtifactListPage = lazy(() => import("../pages/artifacts/list"));
const ArtifactGanttPage = lazy(() => import("../pages/artifacts/gantt"));
const PrognosisPage = lazy(() => import("../pages/prognosis"));
const DiagnosisPage = lazy(() => import("../pages/diagnosis"));
const TagsPage = lazy(() => import("../pages/tags"));
const GoodsPage = lazy(() => import("../pages/goods"));
const CallsPage = lazy(() => import("../pages/calls"));
const AdminPage = lazy(() => import("../pages/admin"));

const DashboardRoutes = ({
  loading = false,
  paths,
}: {
  loading?: boolean;
  paths: string[] | undefined;
}) => {
  const tenantId = useSelector(Selectors.tenantId);
  const userRole = useSelector(Selectors.userRole);
  const isDownLg = useDownBreakpoint("lg");
  const isDownSm = useDownBreakpoint("sm");
  const pageSx: SxProps<Theme> = {
    flex: "1 1 0",
    overflow: "auto",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    // lo spazio della scrollbar è riservato su entrambi i lati, così i margini
    // restano simmetrici e il contenuto non si sposta quando compare
    scrollbarGutter: "stable both-edges",
    px: 3,
    pb: "3px",
    ...(isDownLg && {px: 1, mb: 1}),
    ...(isDownSm && {"& .hide-sm-scroll": {scrollbarWidth: "none"}}),
  };

  if (paths && paths.length === 0 && userRole !== "ADMIN") {
    return (
      <Stack
        bgcolor={"background.default"}
        flex={1}
        gap={4}
        alignItems={"center"}
        justifyContent={"center"}>
        <AppLogo logoSize={200} hideText />
        <Typography color="textPrimary" variant="h3">
          No paths configured for this tenant.
        </Typography>
        <Typography color="textPrimary" variant="h3">
          Please contact your administrator.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack position={"relative"} flex={1}>
      <LoadingView loading={loading} logoAnimation />
      {tenantId && (
        <Suspense fallback={<LoadingView loading logoAnimation />}>
          <Routes>
            <Route path="home" element={<HomePage sx={{...pageSx}} />} />
            <Route
              path="tenants"
              element={<CustomersPage sx={{...pageSx}} />}
            />
            <Route
              path="assets/:assetId?"
              element={<AssetsPage sx={{...pageSx}} />}
            />
            <Route path="groups" element={<GroupsPage sx={{...pageSx}} />} />
            <Route path="users" element={<UsersPage sx={{...pageSx}} />} />
            <Route
              path="users/:userId"
              element={<UserDetailPage sx={{...pageSx}} />}
            />
            <Route path="audits" element={<AuditsPage sx={{...pageSx}} />} />
            <Route
              path="tickets/:ticketId?"
              element={<TicketsPage sx={{...pageSx}} />}
            />
            <Route
              path="tasks/:view/:taskId?"
              element={<TasksPage sx={{...pageSx}} />}
            />
            <Route
              path="documents"
              element={<DocumentsPage sx={{...pageSx}} />}
            />
            <Route
              path="documents/:documentId?"
              element={<DocumentsPage sx={{...pageSx}} />}
            />
            <Route path="archive" element={<ArchivePage sx={{...pageSx}} />} />
            <Route
              path="monitoring"
              element={<MonitoringPage sx={{...pageSx}} />}
            />
            <Route
              path="artifacts"
              element={<ArtifactListPage sx={{...pageSx}} />}
            />
            <Route
              path="artifacts/:artifactId?"
              element={<ArtifactGanttPage sx={{...pageSx}} />}
            />
            <Route
              path="artifacts/gantt/:artifactId?"
              element={<ArtifactGanttPage sx={{...pageSx}} />}
            />
            <Route
              path="prognosis"
              element={<PrognosisPage sx={{...pageSx}} />}
            />
            <Route
              path="diagnosis"
              element={<DiagnosisPage sx={{...pageSx}} />}
            />
            <Route
              path="prognosis"
              element={<PrognosisPage sx={{...pageSx}} />}
            />
            <Route
              path="tags/:tagId?"
              element={<TagsPage sx={{...pageSx}} />}
            />
            <Route path="goods" element={<GoodsPage sx={{...pageSx}} />} />
            <Route path="calls" element={<CallsPage sx={{...pageSx}} />} />
            <Route path="admin" element={<AdminPage sx={{...pageSx}} />} />
            <Route
              path="not-found"
              element={
                <Stack sx={{...pageSx}}>
                  <PageWrapper>
                    <EmptyData
                      Icon={DangerousOutlined}
                      message={translate("error.101")}
                    />
                  </PageWrapper>
                </Stack>
              }
            />
            <Route
              path="*"
              element={<Navigate to={paths?.[0] ?? "/dashboard"} />}
            />
          </Routes>
        </Suspense>
      )}
    </Stack>
  );
};

export {DashboardRoutes};
